import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Modal from '@atlaskit/modal-dialog';

import ErrorBoundary from 'components/Common/ErrorBoundary';
import Loading from 'components/Loading';
import DashboardContext from 'contexts/DashboardContext';
import ReportContext from 'contexts/ReportContext';
import ToastContext from 'contexts/ToastContext';

import StudioBlockEditorContext from './StudioBlockEditorContext';

// Studio's settings, loaded once a block's opened (as Studio itself is)
const StudioBlockEditor = React.lazy(() => import('./StudioBlockEditor'));

// Edits the dashboard's or report's Studio blocks from their menus, in
// Studio's settings modal, without leaving the page (see StudioBlockEditor).
// Above the blocks, which remount once saved.
const StudioBlockEditorProvider = ({
  contentType,
  children,
}: {
  contentType: Studio.ContentType;
  children: JSX.Element | JSX.Element[];
}) => {
  const { dashboard, setIsEditingCard } = useContext(DashboardContext);
  const { report } = useContext(ReportContext);
  const { showToast } = useContext(ToastContext);
  const content = contentType === 'report' ? report : dashboard;
  const [editingCard, setEditingCard] = useState<CanvasCard.Card | undefined>(
    undefined,
  );
  const isEditing = !!editingCard;

  // The dashboard doesn't refresh (remounting its gadgets) while a block's
  // being edited
  useEffect(() => {
    if (!isEditing || contentType !== 'dashboard' || !setIsEditingCard) {
      return;
    }
    setIsEditingCard(true);
    return () => setIsEditingCard(false);
  }, [contentType, isEditing, setIsEditingCard]);

  const openBlockEditor = useCallback(
    (blockId: string) => {
      const canvasCard = content
        ? content.canvas.cards.find((c) => c.layout.i === blockId)
        : undefined;
      setEditingCard(canvasCard);
    },
    [content],
  );
  const close = useCallback(() => setEditingCard(undefined), []);
  const onEditorError = useCallback(() => {
    setEditingCard(undefined);
    showToast("The editor couldn't open. Reload the page to try again.");
  }, [showToast]);

  return (
    <StudioBlockEditorContext.Provider
      value={{ openBlockEditor: content ? openBlockEditor : undefined }}
    >
      {children}
      {editingCard && (
        // A failure (or Studio's code failing to load) closes the editor,
        // leaving the page as it was
        <ErrorBoundary isHiddenError onErrorCallback={onEditorError}>
          <Suspense
            fallback={
              <Modal onClose={close} autoFocus={false}>
                <Loading />
              </Modal>
            }
          >
            <StudioBlockEditor
              key={editingCard.layout.i}
              contentType={contentType}
              canvasCard={editingCard}
              onClose={close}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </StudioBlockEditorContext.Provider>
  );
};

export default StudioBlockEditorProvider;
